import { Divider, Typography, Row, Col, Card, Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import moment from 'moment';
import './QuotationPreview.css';

const { Title, Text } = Typography;

const formatNumber = (num) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(num || 0);

const QuotationPreview = ({ data, overrideCreatedAt }) => {
  if (!data) return null;

  // Print function
  const handlePrint = () => {
    window.print();
  };

  const {
    quotation_no,
    items = [],
    student_name,
    address,
    phone,
    subtotal,
    discount,
    vat,
    total_amount,
    created_by_firstname,
    created_by_lastname,
    unique_id,
    expiration_days = 3 
  } = data;

  // Calculate expiration date based on expiration_days
  const createdAtToUse = overrideCreatedAt || data.created_at;

  const calculateExpirationDate = () => {
    if (!createdAtToUse) return moment().add(data.expiration_days || 3, 'days').format('DD MMM YYYY');
    return moment(createdAtToUse).add(data.expiration_days || 3, 'days').format('DD MMM YYYY');
  };

  // Generate QR code data
  const qrCodeData = unique_id || quotation_no || `QO-${moment().format('YYYYMMDDHHmm')}`;

  // Calculate totals for summary
  const totalAfterDiscount = subtotal - discount;

  // Helper function to get expiration text
  const getExpirationText = () => {
    const days = expiration_days;
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days === 7) return '1 week';
    if (days === 15) return '15 days';
    if (days === 30) return '1 month';
    return `${days} days`;
  };

  return (
    <div id="quotation-preview-content" style={{ 
      width: '100%', 
      fontSize: '13px',
      lineHeight: '1.4'
    }}>
      {/* Header Row - Better Spacing */}
      <Row justify="space-between" align="top" style={{ marginBottom: 20 }}>
        <Col span={14}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <img
              src="/images/Theplanner.png"
              alt="The Planner Education"
              style={{ width: 120, height: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <Title level={4} style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '16px' }}>
                THE PLANNER EDUCATION
              </Title>
              <Text style={{ fontSize: 14, display: 'block', lineHeight: '1.4' }}>
               
                The Mercury Ville @Chidlom 3rd floor<br />
                Lumpini Pathumwan Bangkok<br />
                www.theplannereducation.com<br />
                Tel. 02-253-2533 or 095-726-2666<br />
                Line: @theplanner
              </Text>
            </div>
          </div>
        </Col>
        <Col span={10}>
          <Card
            size="small"
            style={{
              backgroundColor: '#f0f5ff',
              border: '2px solid #d6e4ff',
              textAlign: 'center',
              marginLeft: 'auto',
              maxWidth: 250
            }}
            bodyStyle={{ padding: '12px' }}
          >
            <Title level={3} style={{ color: '#4646ff', margin: '0 0 10px 0', fontSize: '22px' }}>
              QUOTATION
            </Title>
            <div style={{ fontSize: 12 }}>
              <Row justify="space-between" style={{ marginBottom: '4px' }}>
                <Text strong>No:</Text>
                <Text>{quotation_no}</Text>
              </Row>
              <Row justify="space-between" style={{ marginBottom: '4px' }}>
                <Text strong>Date:</Text>
                <Text>{moment(createdAtToUse).format('DD MMM YYYY')}</Text>
              </Row>
              <Row justify="space-between" style={{ marginBottom: '4px' }}>
                <Text strong>Valid for:</Text>
                <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {getExpirationText()}
                </Text>
              </Row>
              <Row justify="space-between">
                <Text strong>Expires:</Text>
                <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {calculateExpirationDate()}
                </Text>
              </Row>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Customer Info - Better Spacing */}
      <Title level={5} style={{ marginBottom: 10, fontSize: '16px' }}>Customer Information</Title>
      <Card 
        size="small" 
        style={{ marginBottom: 18 }}
      >
        <Row gutter={[24, 8]}>
          <Col span={8}>
            <Text strong style={{ fontSize: 14 }}>Name:</Text>
            <Text style={{ fontSize: 14, marginLeft: 8, display: 'block' }}>{student_name || '-'}</Text>
          </Col>
          <Col span={8}>
            <Text strong style={{ fontSize: 14 }}>Phone:</Text>
            <Text style={{ fontSize: 14, marginLeft: 8, display: 'block' }}>{phone || '-'}</Text>
          </Col>
          <Col span={8}>
            <Text strong style={{ fontSize: 14, display: 'block' }}>Address:</Text>
            <Text style={{ fontSize: 14, wordBreak: 'break-word', lineHeight: '1.4' }}>
              {address || '-'}
            </Text>
          </Col>
        </Row>
      </Card>
<Divider style={{ marginTop: 26, marginBottom: 26 }} />

      {/* Items - Better Table */}
      <Title level={5} style={{ marginBottom: 20, fontSize: '16px' }}>Quotation Items</Title>
      <Card 
        size="small" 
        style={{ marginBottom: 18 }}
      >
        {/* Header Row */}
        <div style={{
          backgroundColor: '#f0f0fa',
          padding: '10px 12px',
          borderBottom: '1px solid #d9d9d9',
          fontSize: 14,
          fontWeight: 'bold'
        }}>
          <Row>
            <Col span={2} style={{ textAlign: 'center' }}>#</Col>
            <Col span={12}>Description</Col>
            <Col span={3} style={{ textAlign: 'center' }}>Qty</Col>
            <Col span={3} style={{ textAlign: 'right' }}>Unit Price</Col>
            <Col span={4} style={{ textAlign: 'right' }}>Total</Col>
          </Row>
        </div>

        {/* Item Rows */}
        {items.map((item, index) => (
          <div 
            key={index}
            style={{
              padding: '2px 2px',
              borderBottom: index < items.length - 1 ? '1px solid #f0f0f0' : 'none',
              backgroundColor: index % 2 === 1 ? '#f8f8ff' : 'transparent',
              fontSize: 14
            }}
          >
            <Row align="middle">
              <Col span={2} style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: 12 }}>{index + 1}</Text>
              </Col>
              <Col span={12}>
                <Text style={{ fontSize: 12 }}>{item.description}</Text>
              </Col>
              <Col span={3} style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: 12 }}>{item.quantity}</Text>
              </Col>
              <Col span={3} style={{ textAlign: 'right' }}>
                <Text style={{ fontSize: 12 }}>฿{formatNumber(item.unit_price)}</Text>
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                <Text style={{ fontSize: 12 }}>฿{formatNumber(item.amount)}</Text>
              </Col>
            </Row>
          </div>
        ))}
      </Card>

      {/* Summary and Payment Side by Side - Better Spacing */}
      <Row gutter={24} style={{ marginBottom: 18 }}>
         <Col span={12}>
          <Title level={5} style={{ marginBottom: 10, fontSize: '16px' }}>Payment Information</Title>
          <div style={{
            border: '1px solid #d9d9d9',
            padding: 12,
            backgroundColor: '#fafafa',
            borderRadius: 6,
            fontSize: 14
          }}>
            {[1, 2].map(i => {
              const name = data[`bank${i}_name`];
              if (!name) return null;

              return (
                <div key={i} style={{ marginBottom: i === 1 && data[`bank2_name`] ? 12 : 0 }}>
                  <div style={{ marginBottom: 2 }}>
                    {data[`bank${i}_name`]}
                  </div>
                  {data[`bank${i}_bank_branch`] && (
                    <div style={{ marginBottom: 2 }}>
                      {data[`bank${i}_bank_branch`]}
                    </div>
                  )}
                  <div style={{ marginBottom: 2 }}>
                    {data[`bank${i}_account_name`]}
                  </div>
                  <div>
                    {data[`bank${i}_account_number`]}
                  </div>
                </div>
              );
            })}
          </div>
        </Col>
        <Col span={12}>
          <Title level={5} style={{ marginBottom: 5, fontSize: '16px' }}>Summary</Title>
          <div style={{ 
            fontSize: 14,
            lineHeight: 1.5,
            border: '1px solid #d9d9d9',
            padding: 12,
            backgroundColor: '#fafafa',
            borderRadius: 6
          }}>
            <Row justify="space-between" style={{ marginBottom: 2 }}>
              <Text>Sub Total:</Text>
              <Text>฿{formatNumber(subtotal)}</Text>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 2 }}>
              <Text>Discount:</Text>
              <Text>฿{formatNumber(discount)}</Text>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 2 }}>
              <Text>After discount:</Text>
              <Text>฿{formatNumber(totalAfterDiscount)}</Text>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 2 }}>
              <Text>VAT 7%:</Text>
              <Text>฿{formatNumber(vat)}</Text>
            </Row>
            <Row justify="space-between">
              <Text strong style={{ fontSize: 14 }}>Grand Total:</Text>
              <Text strong style={{ fontSize: 14 }}>
                ฿{formatNumber(total_amount)}
              </Text>
            </Row>
          </div>
        </Col>

        {/* Payment Information */}
       
      </Row>

      {/* Remark Section - Better Spacing */}
      {data.remark && (
        <>
          <Title level={5} style={{ marginBottom: 8, fontSize: '13px', color: '#1890ff' }}>
            Remark
          </Title>
          <div style={{
            fontSize: 12,
            lineHeight: 1.5,
            color: '#0050b3',
            fontStyle: 'italic',
            padding: '10px 12px',
            backgroundColor: '#f0f8ff',
            border: '1px solid #bae7ff',
            borderRadius: 6,
            marginBottom: 20
          }}>
            {data.remark}
          </div>
        </>
      )}

            <div style={{ textAlign: 'center', fontSize: '12px', marginTop: '24px', color: 'red' }}>
  <span>สถาบันขอสงวนสิทธิ์การคืนเงินทุกกรณี : All payments are non-refundable.</span>
</div>


      {/* Footer - Proper Spacing */}
      <Row justify="space-between" align="bottom" style={{ marginTop: 30 }}>
        <Col span={6}>
          <div style={{ textAlign: 'center' }}>
            <QRCodeCanvas
              value={qrCodeData}
              size={70}
              level="M"
            />
            <div style={{ fontSize: 10, marginTop: 4, color: '#666', wordBreak: 'break-all' }}>
              {qrCodeData}
            </div>
          </div>
        </Col>
        
        <Col span={12}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: 12, 
              fontWeight: 'bold', 
              marginBottom: 60,
              color: '#333'
            }}>
              Quoted by
            </div>
            <div style={{ 
              borderTop: '1px solid #333', 
              width: '250px', 
              margin: '0 auto',
              paddingTop: 6,
              fontSize: 13,
              color: '#666'
            }}>
              {created_by_firstname && created_by_lastname && (
                <Text style={{ fontSize: 13 }}>
                  {created_by_firstname} {created_by_lastname}
                </Text>
              )}
            </div>
          </div>
        </Col>
        
        <Col span={6}>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#666' }}>
            <div style={{ fontWeight: 'bold' }}>Printed:</div>
            <div>{moment().format('DD MMM YYYY')}</div>
            <div>{moment().format('HH:mm:ss')}</div>
          </div>
        </Col>
      </Row>
      
      <div style={{ 
        textAlign: 'right', 
        marginTop: 20,
        '@media print': { display: 'none' }
      }} className="no-print">
        <Button 
          type="primary" 
          icon={<PrinterOutlined />} 
          onClick={handlePrint}
          size="small"
        >
          Print Quotation
        </Button>
      </div>

    </div>
    
  );
};

export default QuotationPreview;